import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/i18n/useLocale";
import { OwnerNav } from "@/components/admin/OwnerNav";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Nfc, Loader2, ArrowLeft, ExternalLink, CheckCircle2, Ban, Plus, Copy, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import nfcContent from "@/data/nfc_content.json";

const STATUS_STYLE = {
  ACTIVE: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  UNASSIGNED: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10",
  DEACTIVATED: "text-white/50 border-white/20 bg-white/5",
  LOST: "text-red-300 border-red-400/40 bg-red-400/10",
  REPLACED: "text-white/50 border-white/20 bg-white/5",
};

const fmt = (tpl, vars = {}) =>
  String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] == null ? "" : String(vars[k])));

export default function NfcCards() {
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const { workspace } = useAuth() || {};
  const isAr = String(locale || "en").toLowerCase().startsWith("ar");
  const c = (isAr ? nfcContent.ar : nfcContent.en);

  const [devices, setDevices] = useState(null);
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    api.get("/nfc/devices").then(({ data }) => setDevices(data)).catch(() => setDevices([]));
    api.get("/admin/cards").then(({ data }) => setCards(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const cardById = useMemo(() => Object.fromEntries(cards.map((cc) => [cc.id, cc])), [cards]);

  const setDestination = async (dev, cardId) => {
    if (!cardId || cardId === dev.card_id) return;
    setBusy(dev.token);
    try {
      await api.post("/nfc/activate", { token: dev.token, card_id: cardId });
      toast.success(t("nfc.destinationUpdated"));
      setDevices((ds) => ds.map((d) => d.token === dev.token ? { ...d, card_id: cardId, status: "ACTIVE" } : d));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("nfc.updateFailed"));
    } finally { setBusy(""); }
  };

  const setStatus = async (dev, status) => {
    setBusy(dev.token);
    try {
      await api.post(`/nfc/devices/${dev.token}/status`, { status });
      toast.success(t("nfc.statusUpdated"));
      setDevices((ds) => ds.map((d) => d.token === dev.token ? { ...d, status, card_id: ["DEACTIVATED", "LOST", "UNASSIGNED"].includes(status) ? null : d.card_id } : d));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || t("nfc.updateFailed"));
      toast.error(msg);
    } finally { setBusy(""); }
  };

  const deleteByo = async (dev) => {
    if (!window.confirm(c.list.deleteConfirm)) return;
    setBusy(dev.token);
    try {
      await api.delete(`/nfc/devices/${dev.token}`);
      toast.success(c.list.deleted);
      setDevices((ds) => ds.filter((d) => d.token !== dev.token));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || c.list.deleteFailed);
      toast.error(msg);
    } finally { setBusy(""); }
  };

  return (
    <div className="aria-dark relative min-h-screen bg-[#0B0D12] text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="nfc-page">
      <div className="grain-overlay" style={{ opacity: 0.04 }} />
      <OwnerNav active="nfc" />

      <main className="relative mx-auto max-w-3xl px-4 py-8 sm:px-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/settings")} className="text-white/50 hover:text-white" data-testid="nfc-back"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h2 className="flex items-center gap-2 text-2xl font-light tracking-tight text-white"><Nfc className="h-6 w-6 text-[#D4AF37]" /> {t("nfc.title")}</h2>
              <p className="text-sm text-white/45">{t("nfc.subtitle")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="nfc-add-byo"
            className="inline-flex items-center gap-2 rounded-full border border-[#D6A653]/50 bg-[#D6A653]/10 px-4 py-2 text-sm font-medium text-[#D6A653] hover:bg-[#D6A653]/20"
          >
            <Plus className="h-4 w-4" /> {c.list.addCta}
          </button>
        </div>

        {devices === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
        ) : devices.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/12 py-16 text-center" data-testid="nfc-empty">
            <Nfc className="mx-auto mb-3 h-10 w-10 text-white/25" />
            <p className="text-white/60">{t("nfc.emptyTitle")}</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-white/40">{t("nfc.emptyDesc")}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="nfc-devices">
            {devices.map((dev) => {
              const dest = dev.card_id ? cardById[dev.card_id] : null;
              const disabled = ["DEACTIVATED", "LOST"].includes(dev.status);
              const isByo = (dev.origin || "factory") === "byo";
              return (
                <div key={dev.token} className="rounded-2xl border border-white/10 bg-[#11121A] p-5" data-testid={`nfc-device-${dev.token}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{dev.label || dev.serial || dev.token}</span>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${isByo ? "border-sky-400/40 bg-sky-400/10 text-sky-300" : "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]"}`}
                        data-testid={`nfc-origin-${dev.token}`}
                      >
                        {isByo ? c.list.byoBadge : c.list.officialBadge}
                      </span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_STYLE[dev.status] || STATUS_STYLE.REPLACED}`} data-testid={`nfc-status-${dev.token}`}>{t(`nfc.st_${dev.status}`, { defaultValue: dev.status })}</span>
                    </div>
                    {dest ? (
                      <a href={`/${dest.slug}?src=nfc`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-[#D4AF37] hover:underline" data-testid={`nfc-view-${dev.token}`}><ExternalLink className="h-3 w-3" /> /{dest.slug}</a>
                    ) : null}
                  </div>

                  <div className="mt-4">
                    <label className="text-xs text-white/45">{t("nfc.destinationCard")}</label>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Select value={dev.card_id || ""} onValueChange={(v) => setDestination(dev, v)} disabled={disabled || busy === dev.token}>
                        <SelectTrigger className="h-10 flex-1 text-sm" data-testid={`nfc-select-${dev.token}`}><SelectValue placeholder={t("nfc.chooseCard")} /></SelectTrigger>
                        <SelectContent className="aria-pop">
                          {cards.map((cc) => <SelectItem key={cc.id} value={cc.id}>{cc.identity?.fullName || cc.slug} · /{cc.slug}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {busy === dev.token ? <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" /> : null}
                    </div>
                    <p className="mt-1.5 text-[11px] text-white/35">{t("nfc.routeHint")}</p>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 border-t border-white/8 pt-3">
                    {disabled ? (
                      <button onClick={() => setStatus(dev, "UNASSIGNED")} disabled={busy === dev.token} className="flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-60" data-testid={`nfc-reactivate-${dev.token}`}><CheckCircle2 className="h-3.5 w-3.5" /> {t("nfc.reactivate")}</button>
                    ) : (
                      <button onClick={() => setStatus(dev, "LOST")} disabled={busy === dev.token} className="flex items-center gap-1.5 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/20 disabled:opacity-60" data-testid={`nfc-lost-${dev.token}`}><Ban className="h-3.5 w-3.5" /> {t("nfc.markLost")}</button>
                    )}
                    {isByo ? (
                      <button
                        onClick={() => deleteByo(dev)}
                        disabled={busy === dev.token}
                        data-testid={`nfc-delete-${dev.token}`}
                        className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-60"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {c.list.delete}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {showAdd ? (
        <AddByoDialog
          content={c}
          cards={cards}
          workspaceId={workspace?.id}
          onClose={() => { setShowAdd(false); load(); }}
        />
      ) : null}
    </div>
  );
}

function AddByoDialog({ content, cards, workspaceId, onClose }) {
  const c = content;
  const [step, setStep] = useState(1);
  const [label, setLabel] = useState("");
  const [minting, setMinting] = useState(false);
  const [mint, setMint] = useState(null); // { token, permanent_url, ndef_payload_bytes }
  const [verifying, setVerifying] = useState(false);
  const [tapped, setTapped] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cardId, setCardId] = useState("");
  const [attaching, setAttaching] = useState(false);

  const mintFn = async () => {
    setMinting(true);
    try {
      const { data } = await api.post("/nfc/tokens", { workspace_id: workspaceId, label: label || undefined });
      setMint(data);
      setStep(2);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const err = (typeof detail === "object" && detail?.error) || "";
      if (err === "workspace_quota_exceeded") {
        toast.error(fmt(c.errors.workspaceQuota, { limit: detail.limit }));
      } else if (err === "rate_limit_exceeded") {
        toast.error(fmt(c.errors.rateLimit, { n: detail.retry_after_seconds }));
      } else if (err === "not_a_member") {
        toast.error(c.errors.notMember);
      } else {
        toast.error(typeof detail === "string" ? detail : c.errors.generic);
      }
    } finally { setMinting(false); }
  };

  const copyUrl = () => {
    if (!mint?.permanent_url) return;
    navigator.clipboard.writeText(mint.permanent_url);
    toast.success(c.add.copied);
  };

  // Step 3 polling
  useEffect(() => {
    if (step !== 3 || !mint?.token) return;
    setVerifying(true);
    setTapped(false);
    setElapsed(0);
    let cancelled = false;
    let el = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/nfc/verify/${mint.token}`);
        if (data?.tapped_within_last_120s) {
          if (!cancelled) { setTapped(true); setVerifying(false); }
          return;
        }
      } catch { /* ignore */ }
      el += 3;
      if (!cancelled) setElapsed(el);
      if (!cancelled && el < 120) setTimeout(tick, 3000);
      else if (!cancelled) setVerifying(false);
    };
    tick();
    return () => { cancelled = true; };
  }, [step, mint?.token]);

  const attach = async () => {
    if (!cardId || !mint?.token) return;
    setAttaching(true);
    try {
      await api.post("/nfc/activate", { token: mint.token, card_id: cardId });
      toast.success("NFC chip activated");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not attach card");
    } finally { setAttaching(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="nfc-add-dialog">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-[#D6A653]/30 bg-[#0B0D12] p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-white" data-testid="nfc-add-title">{c.add.title}</h2>
            <p className="mt-1 text-xs text-white/55">{c.add.sub}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="nfc-add-close"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 && (
          <div className="mt-5 space-y-4" data-testid="nfc-add-step1">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={c.add.labelPlaceholder}
              maxLength={80}
              data-testid="nfc-add-label"
              className="w-full rounded-lg border border-white/12 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#D6A653]/60 focus:outline-none"
            />
            <button
              onClick={mintFn}
              disabled={minting}
              data-testid="nfc-add-mint-cta"
              className="w-full rounded-full bg-[#D6A653] px-4 py-2.5 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-60"
            >
              {minting ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {c.add.minting}</span> : c.add.mintCta}
            </button>
          </div>
        )}

        {step >= 2 && mint && (
          <div className="mt-5 space-y-4">
            <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="nfc-add-step1-url">
              <p className="text-xs font-medium uppercase tracking-wider text-[#D6A653]">{c.add.step1Title}</p>
              <p className="mt-1 text-xs text-white/55">{c.add.step1Sub}</p>
              <div className="mt-3 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded border border-white/10 bg-black/60 px-2 py-1.5 text-xs text-white/80" data-testid="nfc-add-url">
                  {mint.permanent_url}
                </code>
                <button onClick={copyUrl} data-testid="nfc-add-copy" className="rounded-lg border border-white/15 p-2 text-white/70 hover:bg-white/5"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              <p className="mt-2 text-[11px] text-white/45" data-testid="nfc-add-bytes">
                {fmt(c.add.bytes, { n: mint.ndef_payload_bytes })}
              </p>
              <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <p className="text-[11px] font-medium uppercase tracking-wider text-white/50">{c.add.capacityTitle}</p>
                <ul className="mt-1.5 space-y-0.5 text-[11px] text-white/55">
                  {c.add.capacityRows.map((r) => (
                    <li key={r.chip} className="flex items-baseline justify-between gap-3">
                      <span>{r.chip}</span><span className="text-white/40">{r.cap}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[10px] text-emerald-300/80">{fmt(c.add.capacityHint, { n: mint.ndef_payload_bytes })}</p>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="nfc-add-step2">
              <p className="text-xs font-medium uppercase tracking-wider text-[#D6A653]">{c.add.step2Title}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-white/65">{c.add.step2Body}</p>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="nfc-add-step3">
              <p className="text-xs font-medium uppercase tracking-wider text-[#D6A653]">{c.add.step3Title}</p>
              {step < 3 ? (
                <button
                  onClick={() => setStep(3)}
                  data-testid="nfc-add-verify-start"
                  className="mt-2 w-full rounded-full border border-white/15 px-3 py-2 text-xs text-white/75 hover:bg-white/5"
                >
                  {c.add.step3Waiting.replace("…", " →")}
                </button>
              ) : tapped ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-300" data-testid="nfc-add-verify-success">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {c.add.step3Success}
                </p>
              ) : verifying ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-white/60" data-testid="nfc-add-verify-waiting">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> {c.add.step3Waiting} <span className="text-white/40">({elapsed}s)</span>
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-white/60" data-testid="nfc-add-verify-timeout">{c.add.step3Timeout}</p>
                  <button onClick={() => setStep(3)} className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/70 hover:bg-white/5">{c.add.step3Poll}</button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="nfc-add-step4">
              <p className="text-xs font-medium uppercase tracking-wider text-[#D6A653]">{c.add.step4Title}</p>
              <p className="mt-1 text-xs text-white/55">{c.add.step4Sub}</p>
              <div className="mt-3">
                <Select value={cardId} onValueChange={setCardId}>
                  <SelectTrigger className="h-10 w-full text-sm" data-testid="nfc-add-card-select">
                    <SelectValue placeholder="Choose a card" />
                  </SelectTrigger>
                  <SelectContent className="aria-pop">
                    {cards.map((cc) => <SelectItem key={cc.id} value={cc.id}>{cc.identity?.fullName || cc.slug} · /{cc.slug}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  onClick={attach}
                  disabled={!cardId || attaching}
                  data-testid="nfc-add-attach"
                  className="mt-3 w-full rounded-full bg-[#D6A653] px-4 py-2 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-60"
                >
                  {attaching ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : c.add.step4Cta}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
